<template>
  <div class="green-fitout__sub-folders">
    <Collapse
      :expand="!isAccepted"
    >
      <template slot="title">
        <HKLLeftRight class="green-fitout-folders__title-box">
          <div class="green-fitout-folders__title-left">
            <span>{{ subFolder.displayOrder }}. {{ subFolder.value }}</span>
            <DottedMenu
              v-if="!isAccepted && canStartGreenFitout"
              :enableClickOutClose="true"
              color="darkblue"
            >
              <HKLButton
                class="delete-button"
                action="delete"
                @click="deleteModalOpen"
              />
              <HKLModal
                ref="deleteModal"
                okBtnAction="yesDelete"
                cancelBtnAction="no"
                :title="$t('tenantProjects.greenFitOut.deleteAlert')"
                @onOkClick="deleteAction"
              />
            </DottedMenu>
          </div>
          <HKLLeftRight class="green-fitout-folders__title-right">
            <template
              v-if="canViewPoint && typeof subFolder.point === 'number'"
              #left
            >
              <PencilEdit
                ref="pointEditor"
                class="green-fitout-folders__point"
                :displayValue="`${subFolder.point} ${$t('tenantProjects.greenFitOut.pts')}`"
                :canEdit="canReviewGreenFitout && !isSent"
                @close="updatePoint"
                @open="() => (inputData = subFolder.point)"
              >
                <HKLInput
                  v-model="inputData"
                  class="green-fitout-folders__point-input"
                  @keydown.enter="pointEditorClose"
                /> {{ $t('tenantProjects.greenFitOut.pts') }}
              </PencilEdit>
            </template>
            <template #right>
              <HKLStatusBadge
                v-if="isAccepted"
                color="light-green"
                :value="$t('status.reviewed') | uppercase"
              />
              <HKLStatusBadge
                v-if="isRejected"
                color="red"
                :value="$t('status.rejected') | uppercase"
              />
            </template>
          </HKLLeftRight>
        </HKLLeftRight>
      </template>
      <template slot="content">
        <div class="green-fitout-folders__description">
          <PencilEdit
            v-if="canStartGreenFitout && !isAccepted"
            ref="descriptionEditor"
            :displayValue="subFolder.description || $t('project.docs.fillDescHint')"
            :canEdit="true"
            @dblclick="() => descriptionEditorOpen()"
            @close="updateDescription"
            @open="() => (inputData = subFolder.description || '')"
          >
            <HKLInput
              v-model="inputData"
              class="green-fitout-folders__description-input"
              @keydown.enter="descriptionEditorClose"
            />
          </PencilEdit>
          <div v-else>
            {{ subFolder.description || '' }}
          </div>
        </div>
        <HkldDropzone
          v-if="isTenant && !isAccepted"
          :sourceId="subFolder.sourceId"
          :metaData="{uploadOnTop: true}"
          @onCompleteQueue="addToNewDocList"
        />
        <EditAttachments
          :sourceId="subFolder.sourceId"
          :canDownloadAll="true"
          :canDelete="(canReviewGreenFitout && !isAccepted) || (isTenant && isRejected)"
          :canEdit="(canReviewGreenFitout || isTenant) && !isAccepted"
          :showDeleteBtnFunc="canDeleteAttachments"
          :uploadOnTop="true"
          :deleteFileCb="deleteFromNewDocList"
        >
          <template
            v-if="canReviewGreenFitout && !isAccepted"
            #buttons
          >
            <HKLButton
              color="darkblue"
              action="rejectAction"
              @click="rejectModalOpen"
            />
            <HKLButton
              color="orange"
              action="reviewedLock"
              @click="acceptModalOpen"
            />
            <HKLModal
              ref="rejectModal"
              okBtnAction="rejectAction"
              :title="$t('tenantProjects.greenFitOut.rejectAlert')"
              :beforeOkClick="formValidate"
              @onOkClick="rejectAction"
            >
              <HKLForm
                ref="form"
                class="text-input"
              >
                <HKLTextarea
                  v-model="inputData"
                  v-fusioninputcheck="{
                    type: 'string',
                    required: true
                  }"
                />
              </HKLForm>
            </HKLModal>
            <HKLModal
              ref="acceptModal"
              class="green-fitout__sub-folders-accept-model"
              okBtnAction="confirmLock"
              :title="$t('tenantProjects.greenFitOut.acceptAlter')"
              :beforeOkClick="formValidate"
              @onOkClick="acceptAction"
            >
              <HKLForm ref="form">
                <HKLInput
                  v-model="inputData"
                  v-fusioninputcheck="{
                    type: 'string',
                    required: true,
                    pattern: numberChecker,
                  }"
                /> {{ $t('tenantProjects.greenFitOut.points') }}
                <p class="hint">
                  {{ $t('tenantProjects.greenFitOut.acceptAlterHint') }}
                </p>
              </HKLForm>
            </HKLModal>
          </template>
        </EditAttachments>
        <GreenFitOutSubFolderHistory :subFolder="subFolder" />
      </template>
    </Collapse>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import HKLStatusBadge from '@/controls/Badge/HKLStatusBadge'
import HKLButton from '@/controls/Button/HKLButton'
import HKLInput from '@/controls/Input/HKLInput'
import HKLModal from '@/controls/Modal/HKLModal'
import HKLForm from '@/controls/Form/HKLForm'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight'
import Collapse from '@/controls/Collapse'
import DottedMenu from '@/controls/DottedMenu'
import EditAttachments from '@/controls/EditAttachments'
import HkldDropzone from '@/controls/HkldDropzone'
import PencilEdit from '@/controls/PencilEdit'
import {
  acceptGreenFitoutFolderSubmission,
  deleteGreenFitoutFolderSubmission,
  rejectGreenFitoutFolderSubmission,
  updateGreenFitoutFolderSubmission,
  updateDocsList,
} from '@/api/tenantProjects/greenFitout.request'
import { reactive, toRefs, computed, unref } from 'vue-demi'
import { GREEN_FITOUT_FOLDER_STATUS, GREEN_FITOUT_DOCS_ACTION, GREEN_FITOUT_FOLDER_ACTIONS, GREEN_FITOUT_HISTORY_ACTION_TYPE } from './consts'
import { useRefModal } from '../../../controls/Modal/modal.hook'
import { useRefForm } from '@/controls/Form/hook'
import GreenFitOutSubFolderHistory from './GreenFitOutSubFolderHistory.vue'
import { useCurrentGreenFitout, useRefreshCurrentGreenFitout, useGreenFitoutPerms, useGreenFitoutHistories } from './hook'
import { useStoreGetters } from '../../../store'
import HKLTextarea from '@/controls/Input/HKLTextarea.vue'
export default {
  components: {
    HKLTextarea,
    DottedMenu,
    Collapse,
    HKLInput,
    HkldDropzone,
    HKLStatusBadge,
    EditAttachments,
    PencilEdit,
    HKLButton,
    HKLModal,
    HKLForm,
    HKLLeftRight,
    GreenFitOutSubFolderHistory,
  },
  props: {
    folder: {
      type: Object,
      default: () => ({}),
    },
    subFolder: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props) {
    const { modal: rejectModal, open: rejectModalOpen } = useRefModal()
    const { modal: acceptModal, open: acceptModalOpen } = useRefModal()
    const { modal: deleteModal, open: deleteModalOpen } = useRefModal()
    const { modal: pointEditor, close: pointEditorClose } = useRefModal()
    const { modal: descriptionEditor, open: descriptionEditorOpen, close: descriptionEditorClose } = useRefModal()
    const { form, validate: formValidate } = useRefForm()
    const { isTenant } = useStoreGetters('user', ['isTenant'])
    const submission = useCurrentGreenFitout()
    const { canViewPoint, canStartGreenFitout, canOnHold, canReviewGreenFitout, canCommentOnGreenFitout } = useGreenFitoutPerms()
    const histories = useGreenFitoutHistories()
    const refresh = useRefreshCurrentGreenFitout()
    const deleteFromNewDocList = async (item) => {
      await updateDocsList(unref(submission).id, {
        docId: item.id,
        action: GREEN_FITOUT_DOCS_ACTION.DELETE,
      })
      if (!unref(isTenant)) {
        await refresh()
      }
    }
    const addToNewDocList = async (uploadedFiles) => {
      await updateDocsList(unref(submission).id, {
        docIds: uploadedFiles.map(item => item.id),
        action: GREEN_FITOUT_DOCS_ACTION.ADD,
      })
    }

    const setupData = reactive({
      canStartGreenFitout,
      canOnHold,
      canReviewGreenFitout,
      canCommentOnGreenFitout,
      canViewPoint,
      isTenant,
      isFolderComplete: computed(() => props.folder.status === GREEN_FITOUT_FOLDER_STATUS.COMPLETED),
      isAccepted: computed(() => {
        return props.subFolder.status === GREEN_FITOUT_FOLDER_STATUS.ACCEPTED
      }),
      isRejected: computed(() => {
        return props.subFolder.status === GREEN_FITOUT_FOLDER_STATUS.REJECTED
      }),
      isSent: computed(() => unref(histories).some((h) => h.actionType === GREEN_FITOUT_HISTORY_ACTION_TYPE.SEND)),
      deleteAction: async () => {
        await deleteGreenFitoutFolderSubmission(unref(submission).id, props.subFolder.sourceId)
        await refresh()
      },
      inputData: '',
      rejectModal,
      rejectModalOpen: () => {
        setupData.inputData = ''
        rejectModalOpen()
      },
      acceptModal,
      acceptModalOpen: () => {
        setupData.inputData = null
        acceptModalOpen()
      },
      numberChecker: (val = '') => {
        const num = Number(val)
        return `${val}`.trim().length && !Number.isNaN(num) && num >= 0
      },
    })
    return {
      ...toRefs(setupData),
      pointEditor,
      pointEditorClose,
      descriptionEditor,
      descriptionEditorOpen,
      descriptionEditorClose,
      deleteFromNewDocList,
      addToNewDocList,
      deleteModal,
      deleteModalOpen,
      form,
      formValidate,
      canDeleteAttachments: (item) => {
        if (unref(isTenant)) {
          const histories = lodash.orderBy(props.subFolder.histories || [], 'createdAt', 'desc')
          const history = histories.find(item => item.action === GREEN_FITOUT_FOLDER_ACTIONS.REJECT)
          return moment(item.createdAt).isBefore(moment(history.createdAt))
        }
        return true
      },
      updatePoint: async () => {
        const point = Number(setupData.inputData)
        if (props.subFolder.point === point || !setupData.numberChecker(point)) return
        await acceptGreenFitoutFolderSubmission(unref(submission).id, props.subFolder.sourceId, { point })
        await refresh()
      },
      updateDescription: async () => {
        if (props.subFolder.description === setupData.inputData) return
        await updateGreenFitoutFolderSubmission(unref(submission).id, props.subFolder.sourceId, { description: setupData.inputData })
        await refresh()
      },
      rejectAction: async () => {
        await rejectGreenFitoutFolderSubmission(unref(submission).id, props.subFolder.sourceId, { comment: setupData.inputData })
        await refresh()
      },
      acceptAction: async () => {
        await acceptGreenFitoutFolderSubmission(unref(submission).id, props.subFolder.sourceId, { point: Number(setupData.inputData) })
        await refresh()
      },
    }
  },
}
</script>

<style lang="scss" scoped>
@import './folders.scss';
.green-fitout__sub-folders {
  ::v-deep {
    .collapse .content {
      padding: 10px;
      border: 1px solid #e0e1e4;
      border-top: none;
    }
    .project-field .field__display {
      margin-bottom: 0;
    }
  }
  &-accept-model {
    input {
      display: inline-block;
      margin-right: 5px;
      width: 100px;
      text-align: right;
    }
    .hint {
      margin-top: 20px;
      color: #0C3A58;
      font-weight: bold;
      font-size: 14px;
    }
  }
  .green-fitout-folders {
    &__description {
      padding-top: 5px;
      padding-bottom: 5px;
      ::v-deep .field__edit {
        width: 100%;
      }
    }
    &__point-input {
      width: 120px;
      display: inline-block;
      text-align: right;
    }
    &__title-left {
      font-weight: bold;
    }
  }
  .green-fitout-folders__title-box {
    .delete-button {
      text-align: center;
    }
  }
}
</style>
